// SearchableSelect.jsx
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

// Same value/onChange/options/placeholder/disabled contract as Select.jsx, so it's a
// drop-in swap anywhere a searchable variant is wanted. `options` is
// [{ value, label, keywords? }]. `keywords` lets an item match on text (e.g. an
// address) that isn't part of its rendered label.
//
// `onSearch` is an optional escape hatch for later: leave it unset and the list is
// filtered client-side (the default, used today). Pass an async `onSearch(query)` and
// the component debounces keystrokes and calls it instead of filtering `options`
// locally — the seam for switching a specific usage to server-side search without
// touching its call site.
export default function SearchableSelect({
  id,
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No results found.",
  disabled = false,
  onSearch,
  searchDebounceMs = 300,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("");
  const [asyncOptions, setAsyncOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const listRef = useRef(null);

  const isAsync = typeof onSearch === "function";
  const items = isAsync ? asyncOptions : options;

  // cmdk keeps the list's scroll position across re-filters — client-side filtering
  // happens internally (the `items` array we render from doesn't change), so this
  // can't key off `items`. Scrolling down to browse and then typing a query left the
  // matching results scrolled out of view (they were there, just below the fold).
  // useLayoutEffect so the scroll resets before paint, avoiding a visible flash of
  // the stale position.
  useLayoutEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [query, items]);

  useEffect(() => {
    if (!value) {
      setSelectedLabel("");
      return;
    }
    const source = isAsync ? asyncOptions : options;
    const match = source.find((option) => option.value === value);
    if (match) setSelectedLabel(match.label);
  }, [value, options, asyncOptions, isAsync]);

  useEffect(() => {
    if (!isAsync || !open) return undefined;

    clearTimeout(debounceRef.current);
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await onSearch(query);
        setAsyncOptions(Array.isArray(results) ? results : []);
      } catch (error) {
        console.error("SearchableSelect: onSearch failed", error);
        setAsyncOptions([]);
      } finally {
        setSearching(false);
      }
    }, searchDebounceMs);

    return () => clearTimeout(debounceRef.current);
  }, [query, isAsync, open, searchDebounceMs, onSearch]);

  const handleSelect = (option) => {
    setSelectedLabel(option.label);
    onChange(option.value);
    setOpen(false);
  };

  const handleOpenChange = (nextOpen) => {
    if (disabled) return;
    setOpen(nextOpen);
    if (!nextOpen) setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          className={cn(
            "flex items-center justify-between border p-4 text-base border-primary-500 bg-white text-black focus:outline-none w-full h-14 rounded-none text-left",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        >
          <span className={cn("truncate", !selectedLabel && "text-gray-500")}>
            {selectedLabel || placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0 rounded-none border-primary-500 bg-white text-black shadow-lg"
      >
        <Command
          shouldFilter={!isAsync}
          className="bg-white text-black"
        >
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            name={id ? `${id}-search` : undefined}
            className="text-black placeholder:text-gray-400"
          />
          <CommandList ref={listRef}>
            <CommandEmpty className="text-gray-500">
              {searching ? (
                <span className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Searching...
                </span>
              ) : (
                emptyMessage
              )}
            </CommandEmpty>
            <CommandGroup>
              {items.map((option, index) => (
                <CommandItem
                  // Not option.value: some upstream datasets have entries sharing the
                  // same value (e.g. two properties recorded under one address), which
                  // would otherwise collide as duplicate React keys.
                  key={`${option.value}-${index}`}
                  value={`${option.label} ${option.keywords || ""}`}
                  onSelect={() => handleSelect(option)}
                  className="text-black data-[selected=true]:bg-gray-100 data-[selected=true]:text-black"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      option.value === value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
